import { toDate } from '@/utils/flowSightPeriods'
import { MATURITY_LABELS, maturityIndexFromDays } from '@/utils/flowSightBuckets'

// One crossfilter reduce per counterparty feeding all three visuals of a
// Portfolio row: the daily sparkline, the maturity bubbles and the average
// maturity tick. Splitting them into three groups would walk the same rows
// three times and let them disagree after a filter.
//
// Volume is NEGOTIATED (DEAL + NOTH.DONE, in BRL) - the tab's chosen basis.
// Rows outside that universe never reach here; Portfolio.vue filters them out
// the same way the Conversions tab does.

// Everything past this lands on the cap. p99 of the June export is 429d and the
// max is 716d - without a cap one client stretches the axis for all 58.
export const MATURITY_AXIS_MAX = 450

// Granularidade do eixo temporal. O reduce SEMPRE acumula por hora; o dia e
// obtido somando as horas na hora de desenhar. E por isso que trocar o periodo
// na sidebar nao precisa reconstruir nada: a janela do RFQ e um filtro do
// crossfilter, nao um rebuild, e a granularidade e decisao de desenho.
export const GRAIN_DAY = 'DAY'
export const GRAIN_HOUR = 'HOUR'

// 'YYYY-MM-DDTHH' - dia e hora na mesma chave, ordenavel como string.
const bucketKey = (value) => {
  const date = toDate(value)
  if (!date) return null
  const p = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}`
}

export const dayOfBucket = (k) => String(k).slice(0, 10)
export const bucketAtGrain = (k, grain) => (grain === GRAIN_HOUR ? String(k) : dayOfBucket(k))

// Soma os baldes horarios dentro da granularidade pedida.
const foldTo = (value, grain) => {
  if (grain === GRAIN_HOUR) return value.byBucket
  const out = new Map()
  value.byBucket.forEach((v, k) => {
    const kk = dayOfBucket(k)
    out.set(kk, (out.get(kk) || 0) + v)
  })
  return out
}

// Uniao ordenada das chaves de todas as linhas, na granularidade pedida - o eixo
// e compartilhado, entao um pico na linha 3 fica na vertical do mesmo instante
// que um pico na linha 1.
export function collectBuckets (rows, grain) {
  const s = new Set()
  rows.forEach(({ value }) => value.byBucket.forEach((_, k) => s.add(bucketAtGrain(k, grain))))
  return Array.from(s).sort()
}

export const emptyClientValue = () => ({
  cnt: 0,
  vol: 0,
  // 'YYYY-MM-DDTHH' -> BRL. Sempre por HORA, mesmo quando o grafico mostra
  // dias - ver GRAIN_*. A Map, not an array: the reduce has to survive
  // removals, and crossfilter calls remove once per row leaving the filter.
  byBucket: new Map(),
  // maturity band index -> BRL. Feeds the stacked bars on the dealer ranking:
  // the band MIX is what separates a client trading 96% inside 30 days from one
  // with 58% past six months.
  byBand: new Map(),
  // Simple mean of maturity in days, per the spec - NOT weighted by volume.
  // Kept as sum/count so add and remove stay exact.
  maturitySum: 0,
  maturityCount: 0,
})

const bump = (map, key, delta) => {
  if (key === null || key === undefined) return
  const next = (map.get(key) || 0) + delta
  // Drop the key at zero so an emptied day/bin disappears instead of lingering
  // as a 0 that still draws a point.
  if (Math.abs(next) < 1e-6) map.delete(key)
  else map.set(key, next)
}

// amountOf(row) must return BRL - pass store.convertNotionalToBRL.
export function createClientPortfolioReducer (amountOf) {
  const apply = (p, v, sign) => {
    const amount = amountOf(v) * sign
    const days = Number(v?.tenorDays) || 0

    p.cnt += sign
    p.vol += amount
    bump(p.byBucket, bucketKey(v?.rfqtimestamp), amount)
    bump(p.byBand, maturityIndexFromDays(days), amount)
    p.maturitySum += days * sign
    p.maturityCount += sign
    return p
  }

  return {
    add: (p, v) => apply(p, v, 1),
    rem: (p, v) => apply(p, v, -1),
    init: emptyClientValue,
  }
}

// `cnt` is the exact emptiness test - `vol` is a float sum kept incrementally
// and does not return to exactly 0 when a filter empties the group.
export const hasRows = (value) => !!value && value.cnt > 0

export const totalVolumeOf = (value) => (hasRows(value) ? value.vol : 0)

export const averageMaturityOf = (value) =>
  (hasRows(value) && value.maturityCount > 0 ? value.maturitySum / value.maturityCount : null)

// [{ day, value }] sorted by day, over the union of days passed in - so every
// row's sparkline shares one x scale and a client with no trade on a given day
// shows a gap at the same place as everyone else.
export function dailySeries (value, keys, grain = GRAIN_DAY) {
  if (!hasRows(value)) return []
  const m = foldTo(value, grain)
  return keys.map((day) => ({ day, value: m.get(day) || 0 }))
}

// Volume divided by number of RFQs - the per-unit metric that reorders the
// ranking: the biggest client by total can be mid-table by ticket, and vice
// versa. Same idea as the reference chart's "per resident adult".
export const averageTicketOf = (value) =>
  (hasRows(value) ? value.vol / value.cnt : 0)

// [{ band, label, value }] over ALL bands, in order, so a stacked bar always
// has the same segment order even when a client skips a band.
export function maturityBandMix (value) {
  if (!hasRows(value)) return []
  return MATURITY_LABELS.map((label, band) => ({
    band,
    label,
    value: value.byBand.get(band) || 0,
  }))
}

// Running total over the shared day axis. Cumulative rather than per-day
// because the exports cover ~20 trading days and the median client trades on
// 5 of them: a daily line is mostly gaps, while a cumulative one is monotone
// and readable with few points.
export function cumulativeSeries (value, keys, grain = GRAIN_DAY) {
  if (!hasRows(value)) return []
  const m = foldTo(value, grain)
  let total = 0
  return keys.map((day) => {
    total += m.get(day) || 0
    return { day, value: total }
  })
}
